import { useState, useEffect } from 'react';
import { apiService } from '@/services/api';
import { MultiSelect, MultiSelectOption } from './MultiSelect';
import { useTranslation } from 'react-i18next';

interface User {
  id: string;
  name: string;
  email: string;
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface UserMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
  disabled?: boolean;
}

export function UserMultiSelect({ value, onChange, className, disabled }: UserMultiSelectProps) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await apiService.get<UsersResponse>('/users');
        // API returns { users: [...], pagination: {...} }
        if (data && Array.isArray(data.users)) {
          setUsers(data.users);
        } else {
          console.warn('Expected users array from /users endpoint, got:', data);
          setUsers([]);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const options: MultiSelectOption[] = Array.isArray(users)
    ? users.map(user => ({
        value: user.id,
        label: `${user.name} (${user.email})`,
      }))
    : [];

  return (
    <MultiSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={loading ? t('common.loading') : t('leads.autoTransfer.selectUsers')}
      searchPlaceholder={t('leads.autoTransfer.searchUsers')}
      className={className}
      disabled={disabled || loading}
    />
  );
}
